import { Fragment, useState, useEffect, useMemo } from 'react';
import './AgentLanding.css';
import { sanitizeHtml } from './sanitizeHtml';
import { formatPhone } from './phone';
import SalesMap from './SalesMap';
import { socialLink } from './social';

// PUBLIC agent profile page (no login) — a lightweight, Zillow-style profile. Fetches
// /api/public/agent/:slug and shows a polished hero, headline stats, about, areas served
// (derived from sold-property cities — no geocoding), a sold-sales grid, and a contact
// form that emails the agent. Linked from email signatures so clicks are tracked.

const INITIAL_PROPERTIES = 6;   // show this many; the rest collapse behind "Show more"
const PROPERTIES_STEP = 50;     // each "Show more" click reveals this many (agents can have 1000s)
const INITIAL_REVIEWS = 6;      // reviews paginate the same way — a busy agent can collect 100s
const REVIEWS_STEP = 50;
const REVIEW_CLIP = 100;        // long reviews collapse to this many chars behind a "more" toggle
const INITIAL_REPORT_GROUPS = 4;   // market reports show the 4 most-recent day-groups first
const REPORT_GROUPS_STEP = 4;
const INITIAL_CHARTS = 6;          // "Charts & Statistics" images revealed at first
const CHARTS_STEP = 12;
const STATUS_LABEL = { sold: 'Sold', active: 'Active', pending: 'Pending' };

// Movable public-profile sections, in DEFAULT order. The agent can rearrange them
// (Profile → Public Profile → Section order); the hero and contact form stay fixed.
export const PROFILE_SECTION_KEYS = ['stats', 'about', 'tools', 'reviews', 'areas', 'properties', 'reports', 'charts', 'calc'];

function normalizeUrl(u) {
  const s = String(u || '').trim();
  if (!s) return '';
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
}
function fmtMoney(n, compact) {
  if (!n) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
    ...(compact ? { notation: 'compact', maximumFractionDigits: 1 } : {}),
  }).format(n);
}
function fmtDate(ymd) {
  if (!ymd) return '';
  const [y, m, d] = String(ymd).split('-').map(Number);
  if (!y || !m || !d) return String(ymd);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
}
function cityLine(l) {
  return [l.city, [l.state, l.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
// "2026-07-19T…" → { key: '2026-07-19', label: '2026-July-19' } for grouping market reports by day.
function reportDay(iso) {
  const s = String(iso || '').slice(0, 10);
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return { key: '0000-00-00', label: 'Undated' };
  return { key: s, label: `${y}-${MONTHS[m - 1]}-${String(d).padStart(2, '0')}` };
}
// Group reports by upload day, newest day first.
function groupReports(reports) {
  const map = new Map();
  for (const r of reports || []) {
    const { key, label } = reportDay(r.date);
    if (!map.has(key)) map.set(key, { key, label, items: [] });
    map.get(key).items.push(r);
  }
  return [...map.values()].sort((a, b) => b.key.localeCompare(a.key));   // descending: most recent first
}

// Read-only 5-star display. `value` 1..5 (rounds to nearest half via width overlay).
function Stars({ value, size = 18 }) {
  const pct = Math.max(0, Math.min(100, (Number(value) / 5) * 100));
  return (
    <span className="al-stars" style={{ fontSize: size }} aria-label={`${value} out of 5 stars`}>
      <span className="al-stars-empty">★★★★★</span>
      <span className="al-stars-full" style={{ width: `${pct}%` }}>★★★★★</span>
    </span>
  );
}

function fmtReviewDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '' : dt.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
}

function AgentLanding({ slug }) {
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading');   // loading | ok | notfound | error
  const [shown, setShown] = useState(INITIAL_PROPERTIES);   // how many property cards are revealed
  const [propQuery, setPropQuery] = useState('');           // address/city/ZIP filter for the list
  const [statusFilter, setStatusFilter] = useState('all');  // 'all' | 'active' | 'pending' | 'sold'
  const [reviewOpen, setReviewOpen] = useState(false);      // inline "Write a review" form
  const [shownReviews, setShownReviews] = useState(INITIAL_REVIEWS);   // how many reviews are revealed
  const [expandedReviews, setExpandedReviews] = useState(() => new Set());   // review ids expanded past the clip
  const [shownGroups, setShownGroups] = useState(INITIAL_REPORT_GROUPS);   // how many market-report day-groups are revealed
  const [shownCharts, setShownCharts] = useState(INITIAL_CHARTS);          // how many chart/statistic images are revealed
  const [toast, setToast] = useState('');                 // transient confirmation ("Link copied")
  const [contactPrefill, setContactPrefill] = useState(''); // seed the contact message (e.g. valuation request)

  useEffect(() => {
    let alive = true;
    fetch(`/api/public/agent/${encodeURIComponent(slug)}`)
      .then(async r => {
        if (r.status === 404) { if (alive) setState('notfound'); return null; }
        if (!r.ok) { if (alive) setState('error'); return null; }
        return r.json();
      })
      .then(d => { if (alive && d) { setData(d); setState('ok'); } })
      .catch(() => { if (alive) setState('error'); });
    return () => { alive = false; };
  }, [slug]);

  // Per-agent SEO — this SPA shares one index.html, so we override the page title,
  // description, canonical, Open Graph, and inject RealEstateAgent structured data at
  // runtime. This is what lets a buyer/seller searching for an agent by name land here
  // with a relevant title/snippet (and Google can show a rich agent card).
  useEffect(() => {
    if (state !== 'ok' || !data) return;
    const a = data.agent || {}, s = data.summary || {}, ps = data.profileStats || {}, rs = data.reviewSummary || {};
    const url = `https://ibetteragent.com/agent/${slug}`;
    const areaNames = [...new Set((data.listings || []).map(l => (l.city || '').trim()).filter(Boolean))];
    const bits = [
      `${a.name} is a real estate agent${a.brokerage ? ` at ${a.brokerage}` : ''}`,
      ps.sinceYear ? `serving clients since ${ps.sinceYear}` : '',
      areaNames.length ? `in ${areaNames.slice(0, 3).join(', ')}` : '',
    ].filter(Boolean).join(' ');
    const stats = [
      s.totalSold ? `${s.totalSold} homes sold` : '',
      s.volume ? `${fmtMoney(s.volume, true)} in sales` : '',
      rs.ratedCount ? `★${rs.avg} from ${rs.count} reviews` : '',
    ].filter(Boolean).join(' · ');
    const title = `${a.name} — Real Estate Agent${a.brokerage ? ` · ${a.brokerage}` : ''} | iBetterAgent`;
    const description = `${bits}.${stats ? ` ${stats}.` : ''} See sold listings, client reviews, and get in touch.`.trim();

    const prevTitle = document.title;
    document.title = title;
    // Each setter records how to undo itself: restore prior content for existing base
    // tags, or remove tags we created. Runs in reverse on unmount so the base meta is
    // never left mangled.
    const restores = [];
    const setMeta = (sel, attr, key, val) => {
      let el = document.head.querySelector(sel);
      if (el) { const prev = el.getAttribute('content'); restores.push(() => el.setAttribute('content', prev)); }
      else { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); restores.push(() => el.remove()); }
      el.setAttribute('content', val);
    };
    setMeta('meta[name="description"]', 'name', 'description', description);
    setMeta('meta[property="og:type"]', 'property', 'og:type', 'profile');
    setMeta('meta[property="og:title"]', 'property', 'og:title', title);
    setMeta('meta[property="og:description"]', 'property', 'og:description', description);
    setMeta('meta[property="og:url"]', 'property', 'og:url', url);
    if (a.photo) setMeta('meta[property="og:image"]', 'property', 'og:image', a.photo);
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    let canon = document.head.querySelector('link[rel="canonical"]');
    if (canon) { const prev = canon.getAttribute('href'); restores.push(() => canon.setAttribute('href', prev)); }
    else { canon = document.createElement('link'); canon.setAttribute('rel', 'canonical'); document.head.appendChild(canon); const c = canon; restores.push(() => c.remove()); }
    canon.setAttribute('href', url);

    // RealEstateAgent JSON-LD (name, brokerage, areas served, rating).
    const ld = {
      '@context': 'https://schema.org', '@type': 'RealEstateAgent',
      name: a.name, url, jobTitle: 'Real Estate Agent',
      ...(a.photo ? { image: a.photo } : {}),
      ...(a.email ? { email: a.email } : {}),
      ...(a.phone ? { telephone: a.phone } : {}),
      ...(a.brokerage ? { worksFor: { '@type': 'Organization', name: a.brokerage } } : {}),
      ...(areaNames.length ? { areaServed: areaNames.slice(0, 10) } : {}),
      ...(rs.ratedCount ? { aggregateRating: { '@type': 'AggregateRating', ratingValue: rs.avg, reviewCount: rs.count, bestRating: 5 } } : {}),
    };
    const ldScript = document.createElement('script');
    ldScript.type = 'application/ld+json';
    ldScript.dataset.seoDyn = '1';
    ldScript.textContent = JSON.stringify(ld);
    document.head.appendChild(ldScript);

    return () => {
      document.title = prevTitle;
      ldScript.remove();
      restores.reverse().forEach(fn => fn());
    };
  }, [state, data, slug]);

  // Active/current listings first, then sold properties newest-first.
  const ordered = useMemo(() => {
    const all = (data?.listings || []).slice();
    const current = all.filter(l => l.status !== 'sold');
    const sold = all.filter(l => l.status === 'sold').sort((a, b) => (b.soldDate || '').localeCompare(a.soldDate || ''));
    return [...current, ...sold];
  }, [data]);
  // Address + status filters + incremental reveal — an agent can have 1000s of sales,
  // so the list grows PROPERTIES_STEP at a time and collapses from top or bottom.
  // Both filters narrow the map pins AND the card list together.
  const q = propQuery.trim().toLowerCase();
  const filtered = ordered.filter(l => {
    if (statusFilter !== 'all' && l.status !== statusFilter) return false;
    if (!q) return true;
    return [l.address, l.city, l.state, l.zip].filter(Boolean).join(' ').toLowerCase().includes(q);
  });
  const visible = filtered.slice(0, shown);
  const filterActive = !!q || statusFilter !== 'all';

  // Areas served — group sold properties by city (lightweight coverage, no map/geocoding).
  const areas = useMemo(() => {
    const m = new Map();
    (data?.listings || []).forEach(l => {
      const city = (l.city || '').trim();
      if (!city) return;
      const k = `${city}${l.state ? ', ' + l.state : ''}`;
      m.set(k, (m.get(k) || 0) + 1);
    });
    return [...m.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
  }, [data]);

  if (state === 'loading') return <div className="al-state">Loading…</div>;
  if (state === 'notfound') return <div className="al-state">This profile isn’t available.</div>;
  if (state === 'error') return <div className="al-state">Something went wrong. Please try again.</div>;

  const a = data.agent;
  const s = data.summary;
  const reviews = data.reviews || [];
  const rs = data.reviewSummary || { count: 0, avg: 0, ratedCount: 0 };
  const ps = data.profileStats || {};
  const LANG_LABEL = { en: 'English', zh: '中文', es: 'Español' };
  const langs = (ps.languages || []).map(c => LANG_LABEL[c]).filter(Boolean);
  // Resolve stored handles/URLs → canonical links (WeChat resolves to a copyable ID, no link).
  const socials = Object.entries(a.socials || {}).map(([k, v]) => socialLink(k, v)).filter(Boolean);
  const firstName = a.name ? a.name.split(' ')[0] : 'me';
  const scrollToContact = () => document.getElementById('al-contact')?.scrollIntoView({ behavior: 'smooth' });
  const scrollToCalc = () => document.getElementById('al-calc')?.scrollIntoView({ behavior: 'smooth' });
  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2200); };
  const profileUrl = `${window.location.origin}/agent/${slug}`;

  // Save contact — download a vCard so a visitor keeps the agent in their phone.
  function saveContact() {
    const v = [
      'BEGIN:VCARD', 'VERSION:3.0', `FN:${a.name}`,
      a.brokerage ? `ORG:${a.brokerage}` : '', 'TITLE:Real Estate Agent',
      a.phone ? `TEL;TYPE=CELL:${a.phone}` : '', a.email ? `EMAIL:${a.email}` : '',
      `URL:${profileUrl}`, 'END:VCARD',
    ].filter(Boolean).join('\r\n');
    const url = URL.createObjectURL(new Blob([v], { type: 'text/vcard' }));
    const link = document.createElement('a');
    link.href = url; link.download = `${a.name || 'agent'}.vcf`;
    document.body.appendChild(link); link.click(); link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    flash('Contact saved');
  }
  // Share — native share sheet on mobile, clipboard fallback on desktop.
  async function shareProfile() {
    if (navigator.share) { try { await navigator.share({ title: `${a.name} — Real Estate Agent`, url: profileUrl }); return; } catch { /* cancelled */ return; } }
    try { await navigator.clipboard.writeText(profileUrl); flash('Link copied'); } catch { flash(profileUrl); }
  }
  // Expand/collapse one long review past its 100-character clip.
  function toggleReviewExpand(id) {
    setExpandedReviews(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  // Seller CTA — jump to the contact form pre-filled with a home-valuation request.
  function requestValuation() {
    setContactPrefill(`Hi ${firstName}, I'd like a free estimate of what my home is worth. My address is: `);
    setTimeout(scrollToContact, 30);
  }

  // ── Reorderable major sections ────────────────────────────────────────────
  // Each entry returns EXACTLY the JSX this page has always rendered — only the
  // render ORDER is agent-configurable (Profile → Public Profile → Section order).
  // Unknown keys are dropped and missing keys appended, so no section can be lost.
  const sectionRenderers = {
    stats: () => (<>
      {/* Headline stats — derived automatically from the sold history */}
      {(s.totalSold > 0 || s.volume > 0) && (
        <>
          <section className="al-stats">
            <div className="al-stat"><span className="al-stat-num">{s.totalSold}</span><span className="al-stat-label">Homes sold</span></div>
            {s.volume > 0 && <div className="al-stat"><span className="al-stat-num">{fmtMoney(s.volume, true)}</span><span className="al-stat-label">Total volume</span></div>}
            <div className="al-stat"><span className="al-stat-num">{s.listingSold}</span><span className="al-stat-label">Seller side</span></div>
            <div className="al-stat"><span className="al-stat-num">{s.buyerSold}</span><span className="al-stat-label">Buyer side</span></div>
          </section>
          {ps.priceStats && ps.priceStats.max > 0 && (
            <p className="al-price-fit">
              Homes from <strong>{fmtMoney(ps.priceStats.min)}</strong> to <strong>{fmtMoney(ps.priceStats.max)}</strong>
              {' '}· median <strong>{fmtMoney(ps.priceStats.median)}</strong>
            </p>
          )}
        </>
      )}
    </>),
    about: () => (<>
      {/* About */}
      {a.bio && (
        <section className="al-section">
          <h2 className="al-h2">{sectionTitle('about') || <>About {firstName}</>}</h2>
          <div className="al-about al-richtext" dangerouslySetInnerHTML={{ __html: sanitizeHtml(a.bio) }} />
        </section>
      )}
    </>),
    tools: () => (<>
      {/* Free buyer/seller tools — an emphasized call-to-action to capture leads */}
      <section className="al-tools">
        <div className="al-tools-inner">
          <div className="al-tools-actions">
            <button type="button" className="al-cta al-cta-gold" onClick={requestValuation}>🏡 What's my home worth?</button>
            <button type="button" className="al-cta al-cta-ghost" onClick={scrollToCalc}>💰 How much can I afford?</button>
          </div>
        </div>
      </section>
    </>),
    reviews: () => (<>
      {/* Reviews — approved testimonials. Always present: clients can leave a review
          right here (no emailed link needed); it goes to the agent as PENDING and only
          shows after approval. */}
      <section className="al-section">
        <div className="al-reviews-head">
          <h2 className="al-h2">{sectionTitle('reviews') || 'Client reviews'}</h2>
          {rs.ratedCount > 0 && (
            <div className="al-rating-badge">
              <Stars value={rs.avg} size={18} />
              <span className="al-rating-num">{rs.avg.toFixed(1)}</span>
              <span className="al-rating-count">· {rs.count} review{rs.count === 1 ? '' : 's'}</span>
            </div>
          )}
          {shownReviews > INITIAL_REVIEWS && (
            <button type="button" className="al-more al-more-top" onClick={() => setShownReviews(INITIAL_REVIEWS)}>Show less</button>
          )}
          <button type="button" className="al-write-review" onClick={() => setReviewOpen(v => !v)}>
            {reviewOpen ? 'Close' : '★ Write a review'}
          </button>
        </div>
        {reviewOpen && <WriteReview slug={slug} agentName={firstName} onDone={() => setReviewOpen(false)} />}
        {reviews.length === 0
          ? <p className="al-empty">No reviews yet — worked with {firstName}? Be the first to share your experience.</p>
          : (
            <>
              <div className="al-reviews">
                {reviews.slice(0, shownReviews).map(rv => {
                  // Long reviews collapse to their first ~100 characters with a "more"
                  // toggle, so a few essays can't take over the whole page.
                  const body = String(rv.body || '');
                  const isLong = body.length > REVIEW_CLIP + 40;   // don't clip for a trivial saving
                  const expanded = expandedReviews.has(rv.id);
                  const shownText = isLong && !expanded ? body.slice(0, REVIEW_CLIP).trimEnd() + '…' : body;
                  return (
                    <figure key={rv.id} className="al-review">
                      {typeof rv.rating === 'number' && rv.rating > 0 && <Stars value={rv.rating} size={16} />}
                      <blockquote className="al-review-body">{shownText}</blockquote>
                      {isLong && (
                        <button type="button" className="al-review-more" onClick={() => toggleReviewExpand(rv.id)}>
                          {expanded ? '− less' : '+ more…'}
                        </button>
                      )}
                      <figcaption className="al-review-meta">
                        <span className="al-review-author">{rv.authorName}</span>
                        {fmtReviewDate(rv.date) && <span className="al-review-date"> · {fmtReviewDate(rv.date)}</span>}
                      </figcaption>
                    </figure>
                  );
                })}
              </div>
              {(reviews.length > shownReviews || shownReviews > INITIAL_REVIEWS) && (
                <div className="al-more-row">
                  {reviews.length > shownReviews && (
                    <button type="button" className="al-more" onClick={() => setShownReviews(s => s + REVIEWS_STEP)}>
                      Show {Math.min(REVIEWS_STEP, reviews.length - shownReviews)} more ({reviews.length - shownReviews} remaining)
                    </button>
                  )}
                  {shownReviews > INITIAL_REVIEWS && (
                    <button type="button" className="al-more" onClick={() => setShownReviews(INITIAL_REVIEWS)}>Show less</button>
                  )}
                </div>
              )}
            </>
          )}
      </section>
    </>),
    areas: () => (<>
      {/* Areas served */}
      {areas.length > 0 && (
        <section className="al-section">
          <h2 className="al-h2">{sectionTitle('areas') || 'Areas served'}</h2>
          <div className="al-areas">
            {areas.map(ar => (
              <span key={ar.name} className="al-area-chip">{ar.name}<span className="al-area-count">{ar.count}</span></span>
            ))}
          </div>
        </section>
      )}
    </>),
    properties: () => (<>
      {/* Properties — one section: heading, filters (address + status), then the
          sales map, then the paged card list. Both filters narrow the map pins AND
          the list together, just like the agent's own Properties screen. */}
      {ordered.length > 0 && (
        <section className="al-section">
          <div className="al-props-head">
            <h2 className="al-h2">{sectionTitle('properties') || 'Properties'}</h2>
            {shown > INITIAL_PROPERTIES && (
              <button type="button" className="al-more al-more-top" onClick={() => setShown(INITIAL_PROPERTIES)}>Show less</button>
            )}
          </div>

          <div className="al-prop-filter">
            <input className="al-prop-search" type="text"
              placeholder="Filter by address, city, or ZIP…"
              value={propQuery}
              onChange={e => { setPropQuery(e.target.value); setShown(INITIAL_PROPERTIES); }} />
            <select className="al-prop-status" value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setShown(INITIAL_PROPERTIES); }}>
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="sold">Sold</option>
            </select>
            <span className="al-prop-count">
              {filterActive ? `${filtered.length} of ` : ''}{ordered.length} propert{ordered.length !== 1 ? 'ies' : 'y'}
            </span>
          </div>

          {/* Map reflects the SAME filtered set as the list below. */}
          {(() => {
            const pts = filtered
              .filter(l => Number.isFinite(l.lat) && Number.isFinite(l.lng))
              .map((l, i) => ({
                id: `${l.address}-${i}`, lat: l.lat, lng: l.lng, status: l.status,
                title: l.address || l.city || 'Property',
                lines: [
                  cityLine(l),
                  [l.role === 'buyer' ? 'Buyer side' : 'Seller side',
                   l.status === 'sold' && l.soldDate ? `Sold ${fmtDate(l.soldDate)}` : STATUS_LABEL[l.status] || ''].filter(Boolean).join(' · '),
                  l.price > 0 ? fmtMoney(l.price) : '',
                ].filter(Boolean),
              }));
            // Mount the map when ANY property is mappable (unfiltered set) — the live
            // filter only changes the pins. Unmounting Leaflet on a filter keystroke
            // is what crashed (zoom-transition callback on a destroyed map).
            const hasGeo = ordered.some(l => Number.isFinite(l.lat) && Number.isFinite(l.lng));
            return hasGeo ? <div className="al-prop-map"><SalesMap points={pts} height={400} /></div> : null;
          })()}

          {filtered.length === 0
            ? <p className="al-empty">No properties match your filter.</p>
            : (
              <>
                <div className="al-grid">
                  {visible.map((l, i) => (
                    <div key={i} className="al-card">
                      <div className="al-card-top">
                        {l.price > 0 && <div className="al-card-price">{fmtMoney(l.price)}</div>}
                        <span className={`al-tag al-status-${l.status}`}>{STATUS_LABEL[l.status] || 'Active'}</span>
                      </div>
                      <div className="al-card-addr">{l.address || '—'}</div>
                      {cityLine(l) && <div className="al-card-city">{cityLine(l)}</div>}
                      <div className="al-card-foot">
                        <span className={`al-role-pill al-role-${l.role}`}>{l.role === 'buyer' ? 'Buyer side' : 'Seller side'}</span>
                        {l.status === 'sold' && l.soldDate && <span className="al-card-sold">Sold {fmtDate(l.soldDate)}</span>}
                      </div>
                    </div>
                  ))}
                </div>
                {(filtered.length > shown || shown > INITIAL_PROPERTIES) && (
                  <div className="al-more-row">
                    {filtered.length > shown && (
                      <button type="button" className="al-more" onClick={() => setShown(s => s + PROPERTIES_STEP)}>
                        Show {Math.min(PROPERTIES_STEP, filtered.length - shown)} more ({filtered.length - shown} remaining)
                      </button>
                    )}
                    {shown > INITIAL_PROPERTIES && (
                      <button type="button" className="al-more" onClick={() => setShown(INITIAL_PROPERTIES)}>Show less</button>
                    )}
                  </div>
                )}
              </>
            )}
        </section>
      )}
    </>),
    reports: () => (<>
      {/* Market Reports & Stats — files the agent published, grouped by the day they
          were added (newest day first). Only the most-recent INITIAL_REPORT_GROUPS
          groups show; the rest reveal with Show more / Show less. */}
      {Array.isArray(data.marketReports) && data.marketReports.length > 0 && (() => {
        const groups = groupReports(data.marketReports);
        const visibleGroups = groups.slice(0, shownGroups);
        return (
          <section className="al-section">
            <div className="al-props-head">
              <h2 className="al-h2">{sectionTitle('reports') || 'Market Reports'}</h2>
              {shownGroups > INITIAL_REPORT_GROUPS && (
                <button type="button" className="al-more al-more-top" onClick={() => setShownGroups(INITIAL_REPORT_GROUPS)}>Show less</button>
              )}
            </div>
            {visibleGroups.map(g => (
              <div key={g.key} className="al-report-group">
                <div className="al-report-date">{g.label}</div>
                <ul className="al-reports">
                  {g.items.map(r => (
                    <li key={r.id} className="al-report">
                      <a className="al-report-link" href={`/api/public/agent/${encodeURIComponent(slug)}/file/${encodeURIComponent(r.id)}`}
                        target="_blank" rel="noopener noreferrer">
                        <span className="al-report-ico">📄</span>
                        <span className="al-report-title">{r.title}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            {(groups.length > shownGroups || shownGroups > INITIAL_REPORT_GROUPS) && (
              <div className="al-more-row">
                {groups.length > shownGroups && (
                  <button type="button" className="al-more" onClick={() => setShownGroups(s => s + REPORT_GROUPS_STEP)}>
                    Show {Math.min(REPORT_GROUPS_STEP, groups.length - shownGroups)} more ({groups.length - shownGroups} day{groups.length - shownGroups !== 1 ? 's' : ''} remaining)
                  </button>
                )}
                {shownGroups > INITIAL_REPORT_GROUPS && (
                  <button type="button" className="al-more" onClick={() => setShownGroups(INITIAL_REPORT_GROUPS)}>Show less</button>
                )}
              </div>
            )}
          </section>
        );
      })()}
    </>),
    charts: () => (<>
      {/* Charts & Statistics — images the agent published (Graphs & Images marked
          Public). Rendered inline, newest first; each opens full-size in a new tab. */}
      {Array.isArray(data.chartImages) && data.chartImages.length > 0 && (() => {
        const charts = [...data.chartImages].sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
        const visibleCharts = charts.slice(0, shownCharts);
        const imgUrl = (c) => `/api/public/agent/${encodeURIComponent(slug)}/file/${encodeURIComponent(c.id)}`;
        return (
          <section className="al-section">
            <div className="al-props-head">
              <h2 className="al-h2">{sectionTitle('charts') || <>Charts &amp; Statistics</>}</h2>
              {shownCharts > INITIAL_CHARTS && (
                <button type="button" className="al-more al-more-top" onClick={() => setShownCharts(INITIAL_CHARTS)}>Show less</button>
              )}
            </div>
            <div className="al-charts">
              {visibleCharts.map(c => (
                <figure key={c.id} className="al-chart">
                  <a href={imgUrl(c)} target="_blank" rel="noopener noreferrer" title="Open full size">
                    <img src={imgUrl(c)} alt={c.title} loading="lazy" />
                  </a>
                  <figcaption>{c.title}</figcaption>
                </figure>
              ))}
            </div>
            {(charts.length > shownCharts || shownCharts > INITIAL_CHARTS) && (
              <div className="al-more-row">
                {charts.length > shownCharts && (
                  <button type="button" className="al-more" onClick={() => setShownCharts(s => s + CHARTS_STEP)}>
                    Show {Math.min(CHARTS_STEP, charts.length - shownCharts)} more ({charts.length - shownCharts} remaining)
                  </button>
                )}
                {shownCharts > INITIAL_CHARTS && (
                  <button type="button" className="al-more" onClick={() => setShownCharts(INITIAL_CHARTS)}>Show less</button>
                )}
              </div>
            )}
          </section>
        );
      })()}
    </>),
    calc: () => (<>
      {/* Buyer tool — estimate a monthly payment (client-side, no agent effort). */}
      <MortgageCalc defaultPrice={ps.priceStats && ps.priceStats.median} agentName={firstName} onContact={scrollToContact} title={sectionTitle('calc')} />
    </>),
  };
  const orderedSections = Array.isArray(a.sectionOrder)
    ? a.sectionOrder.filter(k => PROFILE_SECTION_KEYS.includes(k))
    : [];
  for (const k of PROFILE_SECTION_KEYS) if (!orderedSections.includes(k)) orderedSections.push(k);
  // Sections the agent unchecked in Profile → Section Order are dropped entirely.
  const hiddenSections = new Set(Array.isArray(a.hiddenSections) ? a.hiddenSections : []);
  const visibleSections = orderedSections.filter(k => !hiddenSections.has(k));
  // Agent-customized section heading (any language), or null to use the default.
  const sectionTitle = (key) => {
    const t = a.sectionTitles && typeof a.sectionTitles === 'object' ? a.sectionTitles[key] : null;
    return typeof t === 'string' && t.trim() ? t.trim() : null;
  };

  return (
    <div className="al-page">
      {/* Hero */}
      <header className="al-hero">
        <div className="al-hero-inner">
          {a.photo
            ? <img className="al-photo" src={a.photo} alt={a.name} />
            : <div className="al-photo al-photo-initial">{(a.name || '?').charAt(0).toUpperCase()}</div>}
          <div className="al-hero-text">
            <h1 className="al-name">{a.name}</h1>
            {!a.paid && (
              <a className="al-powered" href="https://ibetteragent.com" target="_blank" rel="noreferrer" title="Real-estate client follow-up, automated">
                powered by <strong>iBetterAgent.com</strong>
              </a>
            )}
            <div className="al-subtitle">
              <span className="al-role">Real Estate Agent</span>
              {a.brokerage && <span className="al-brokerage"> · {a.brokerage}</span>}
              {ps.sinceYear && <span className="al-since"> · Serving clients since {ps.sinceYear}</span>}
            </div>
            {langs.length > 0 && (
              <div className="al-langs" title="Languages spoken">
                <span className="al-langs-icon">🌐</span>
                {langs.map(l => <span key={l} className="al-lang-chip">{l}</span>)}
              </div>
            )}
            {a.email && (
              <div className="al-hero-email">
                <a className="al-link" href={`mailto:${a.email}`}>✉️ {a.email}</a>
              </div>
            )}
            <div className="al-hero-actions">
              <button type="button" className="al-cta" onClick={scrollToContact}>Contact {firstName}</button>
              {a.phone && <a className="al-cta al-cta-ghost" href={`tel:${a.phone}`}>📞 {a.phone}</a>}
              <button type="button" className="al-cta al-cta-ghost" onClick={saveContact} title="Save to your contacts">＋ Save contact</button>
              <button type="button" className="al-cta al-cta-ghost" onClick={shareProfile} title="Share this profile">↗ Share</button>
            </div>
            {(a.website || socials.length > 0) && (
              <div className="al-links">
                {a.website && <a className="al-link" href={normalizeUrl(a.website)} target="_blank" rel="noreferrer">🌐 Website</a>}
                {socials.map((s) => (s.isLink ? (
                  <a key={s.platform} className="al-link" href={s.url} target="_blank" rel="noreferrer">
                    {s.icon} {s.display}
                  </a>
                ) : (
                  <button
                    key={s.platform}
                    type="button"
                    className="al-link"
                    title="Copy WeChat ID"
                    onClick={async () => {
                      try { await navigator.clipboard.writeText(s.display); flash('WeChat ID copied'); }
                      catch { flash(s.display); }
                    }}
                  >
                    {s.icon} {s.label}: {s.display}
                  </button>
                )))}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Major sections in the agent's chosen order (default order when unset). */}
      {visibleSections.map(k => <Fragment key={k}>{sectionRenderers[k]()}</Fragment>)}

      <ContactForm slug={slug} agentName={firstName} initialMessage={contactPrefill} />

      <footer className="al-footer">
        Powered by <a className="al-footer-link" href="https://ibetteragent.com" target="_blank" rel="noopener noreferrer">iBetterAgent.com</a>
      </footer>

      {toast && <div className="al-toast" role="status">{toast}</div>}
    </div>
  );
}

// Mortgage payment estimator — pure client-side, genuinely useful for buyers browsing.
// Not financial advice; a footnote says so. Defaults the price to the agent's median sale.
function MortgageCalc({ defaultPrice, agentName, onContact, title }) {
  const [price, setPrice] = useState(Math.round((defaultPrice || 750000) / 1000) * 1000);
  const [downPct, setDownPct] = useState(20);
  const [rate, setRate] = useState(6.5);
  const [years, setYears] = useState(30);
  const money = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Math.max(0, Math.round(n)));
  const loan = Math.max(0, price - price * (downPct / 100));
  const r = rate / 100 / 12, n = years * 12;
  const monthly = r > 0 ? (loan * r) / (1 - Math.pow(1 + r, -n)) : (n ? loan / n : 0);
  const num = (v, set, min, max) => set(Math.min(max, Math.max(min, Number(v) || 0)));
  return (
    <section id="al-calc" className="al-section al-calc">
      <h2 className="al-h2">{title || 'Estimate your monthly payment'}</h2>
      <div className="al-calc-grid">
        <div className="al-calc-inputs">
          <label className="al-calc-field"><span>Home price</span>
            <input type="number" min="0" step="10000" value={price} onChange={e => num(e.target.value, setPrice, 0, 100000000)} /></label>
          <label className="al-calc-field"><span>Down payment ({downPct}%)</span>
            <input type="range" min="0" max="100" step="1" value={downPct} onChange={e => setDownPct(Number(e.target.value))} />
            <em>{money(price * downPct / 100)} down</em></label>
          <label className="al-calc-field"><span>Interest rate (%)</span>
            <input type="number" min="0" max="25" step="0.1" value={rate} onChange={e => num(e.target.value, setRate, 0, 25)} /></label>
          <label className="al-calc-field"><span>Loan term (years)</span>
            <select value={years} onChange={e => setYears(Number(e.target.value))}>
              <option value={30}>30 years</option><option value={20}>20 years</option><option value={15}>15 years</option><option value={10}>10 years</option>
            </select></label>
        </div>
        <div className="al-calc-out">
          <div className="al-calc-out-label">Estimated principal &amp; interest</div>
          <div className="al-calc-out-num">{money(monthly)}<span>/mo</span></div>
          <div className="al-calc-out-sub">on a {money(loan)} loan</div>
          <button type="button" className="al-cta al-calc-cta" onClick={onContact}>Ask {agentName} about financing</button>
        </div>
      </div>
      <p className="al-calc-note">Estimate only — principal &amp; interest, excluding taxes, insurance, and HOA. Not a loan offer or financial advice.</p>
    </section>
  );
}

// Inline walk-in review form — no emailed link needed. Submits to the public
// endpoint; the review lands as PENDING in the agent's Reviews tab and only
// appears here after the agent approves it.
function WriteReview({ slug, agentName, onDone }) {
  const [form, setForm] = useState({ name: '', email: '', body: '', company: '' });
  const [stars, setStars] = useState(0);
  const [status, setStatus] = useState('');   // '' | sending | sent
  const [err, setErr] = useState('');
  const set = k => e => { setForm(f => ({ ...f, [k]: e.target.value })); setErr(''); };

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.body.trim()) { setErr('Please add your name, email, and a few words.'); return; }
    setStatus('sending'); setErr('');
    try {
      const r = await fetch(`/api/public/agent/${encodeURIComponent(slug)}/review`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name, email: form.email, body: form.body, company: form.company, ...(stars ? { rating: stars } : {}) }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Could not submit your review.');
      setStatus('sent');
    } catch (e2) { setStatus(''); setErr(e2.message || 'Could not submit your review.'); }
  }

  if (status === 'sent') {
    return (
      <div className="al-review-form al-review-form-done">
        Thank you for your review! {agentName} appreciates it very much. 🙏
      </div>
    );
  }
  return (
    <form className="al-review-form" onSubmit={submit}>
      <input className="al-hp" tabIndex={-1} autoComplete="off" value={form.company} onChange={set('company')} aria-hidden="true" />
      <div className="al-form-row">
        <input className="al-input" placeholder="Your name *" value={form.name} onChange={set('name')} />
        <input className="al-input" type="email" placeholder="Your email *" value={form.email} onChange={set('email')} />
      </div>
      <div className="al-star-pick" role="radiogroup" aria-label="Rating (optional)">
        {[1, 2, 3, 4, 5].map(n => (
          <button key={n} type="button"
            className={`al-star-btn ${n <= stars ? 'on' : ''}`}
            aria-label={`${n} star${n > 1 ? 's' : ''}`}
            onClick={() => setStars(n === stars ? 0 : n)}>★</button>
        ))}
      </div>
      <textarea className="al-textarea" rows={4} placeholder={`How was your experience working with ${agentName}? *`}
        value={form.body} onChange={set('body')} />
      {err && <div className="al-form-err">{err}</div>}
      <div className="al-review-form-actions">
        <button className="al-send" type="submit" disabled={status === 'sending'}>
          {status === 'sending' ? 'Submitting…' : 'Submit review'}
        </button>
        <span className="al-review-form-note">Your email is only shown to {agentName}, never published.</span>
      </div>
    </form>
  );
}

function ContactForm({ slug, agentName, initialMessage }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', company: '' });
  const [status, setStatus] = useState('');   // '' | sending | sent | error
  const [err, setErr] = useState('');
  const set = k => e => { setForm(f => ({ ...f, [k]: e.target.value })); setErr(''); };
  // Adopt a prefilled message (e.g. the "What's my home worth?" CTA) when it changes.
  useEffect(() => { if (initialMessage) { setForm(f => ({ ...f, message: initialMessage })); setStatus(''); } }, [initialMessage]);

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) { setErr('Please add your name and a message.'); return; }
    setStatus('sending'); setErr('');
    try {
      const r = await fetch(`/api/public/agent/${encodeURIComponent(slug)}/contact`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Could not send your message.');
      setStatus('sent');
    } catch (e2) { setStatus('error'); setErr(e2.message || 'Could not send your message.'); }
  }

  if (status === 'sent') {
    return (
      <section id="al-contact" className="al-section">
        <h2 className="al-h2">Message sent ✓</h2>
        <div className="al-contact-card">
          <p className="al-contact-done">Thanks — {agentName} will get back to you soon.</p>
        </div>
      </section>
    );
  }

  return (
    <section id="al-contact" className="al-section">
      <h2 className="al-h2">Contact {agentName}</h2>
      <div className="al-contact-card">
      <form onSubmit={submit} className="al-form">
        <input className="al-hp" tabIndex={-1} autoComplete="off" value={form.company} onChange={set('company')} aria-hidden="true" />
        <div className="al-form-row">
          <input className="al-input" placeholder="Your name *" value={form.name} onChange={set('name')} />
          <input className="al-input" type="email" placeholder="Your email" value={form.email} onChange={set('email')} />
        </div>
        <input className="al-input" type="tel" inputMode="numeric" maxLength={12} placeholder="Your phone (optional)"
          value={form.phone} onChange={e => { setForm(f => ({ ...f, phone: formatPhone(e.target.value) })); setErr(''); }} />
        <textarea className="al-textarea" rows={4} placeholder="How can I help? *" value={form.message} onChange={set('message')} />
        {err && <div className="al-form-err">{err}</div>}
        <button className="al-send" type="submit" disabled={status === 'sending'}>
          {status === 'sending' ? 'Sending…' : 'Send message'}
        </button>
      </form>
      </div>
    </section>
  );
}

export default AgentLanding;
